/**
 * Simple Carousel Component for Homepage Sections
 * Horizontal scroll with navigation arrows and touch support
 */
import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const CarouselSimple = ({ 
  children, 
  className = '',
  itemClassName = '',
  showArrows = true,
  showIndicators = false,
  autoScroll = false,
  autoScrollInterval = 5000,
  gap = 24, // gap in pixels
}) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Check scroll position and update arrow visibility
  const checkScrollPosition = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      checkScrollPosition();
      scrollContainer.addEventListener('scroll', checkScrollPosition);
      window.addEventListener('resize', checkScrollPosition);
      return () => {
        scrollContainer.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, [children]);

  // Auto scroll
  useEffect(() => {
    if (!autoScroll || !scrollRef.current) return;
    
    const interval = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        if (scrollLeft >= scrollWidth - clientWidth - 10) {
          scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scroll('right');
        }
      }
    }, autoScrollInterval);

    return () => clearInterval(interval);
  }, [autoScroll, autoScrollInterval]);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = scrollRef.current.clientWidth * 0.8;
    const newScrollLeft = direction === 'left' 
      ? scrollRef.current.scrollLeft - scrollAmount
      : scrollRef.current.scrollLeft + scrollAmount;
    
    scrollRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  return (
    <div className={`relative group ${className}`}>
      {/* Left Arrow */}
      {showArrows && canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:scale-110 -ml-4 md:-ml-6"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-6 h-6 text-stone-700" />
        </button>
      )}

      {/* Right Arrow */}
      {showArrows && canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:scale-110 -mr-4 md:-mr-6"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-6 h-6 text-stone-700" />
        </button>
      )}

      {/* Left fade gradient */}
      {canScrollLeft && (
        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-inherit to-transparent z-10 pointer-events-none" />
      )}

      {/* Right fade gradient with scroll indicator */}
      {canScrollRight && (
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-inherit via-inherit/80 to-transparent z-10 pointer-events-none flex items-center justify-end pr-2">
          <div className="flex items-center gap-1 text-stone-400 text-xs font-medium opacity-60 group-hover:opacity-0 transition-opacity">
            <ChevronRight className="w-4 h-4 animate-pulse" />
          </div>
        </div>
      )}

      {/* Scrollable container */}
      <div
        ref={scrollRef}
        className="flex overflow-x-auto scrollbar-hide scroll-smooth pb-4 -mx-6 px-6"
        style={{ gap: `${gap}px` }}
      >
        {React.Children.map(children, (child, index) => (
          <div 
            key={index} 
            className={`flex-shrink-0 ${itemClassName}`}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            {child}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CarouselSimple;
